import type { Ref } from 'vue'
import type { AppTextareaEvent, NativeSelectOption } from '@shared/ui/fields'
import type { DropZoneAcceptedFile, DropZoneItemPayload, DropZoneUpload } from '@shared/ui/other'

import { useListsStore } from '@shared/model'
import { DROP_ZONE_STATUS } from '@shared/ui/other'

import { DisciplineFilesModal } from './enums'
import { useDisciplineStore } from './store'
import { useDisciplineFilesStore } from './files/store'
import { useDisciplineFilesApi } from '../api/files.hook'
import { useDisciplineFileUploadApi } from '../api/file-upload.hook'
import { useDisciplineFileDeleteApi } from '../api/file-delete.hook'
import {
  useDisciplineFilePackDeleteApi,
  useDisciplineFilePackSendApi,
  useDisciplineFilePackStoreApi,
} from '../api/file-pack.hook'
import { getTaskOptionText } from './discipline-files'

interface ModalDisciplineFilesSendReturn {
  refIsOpen: ComputedRef<boolean>
  refIsLoading: Ref<boolean>
  refTitle: ComputedRef<string>
  refCategoryOptions: ComputedRef<NativeSelectOption[]>
  refTaskOptions: ComputedRef<NativeSelectOption[]>
  refCategory: Ref<string>
  refTutorId: Ref<number | null>
  refTaskId: Ref<number | null>
  refComment: Ref<string>
  refUploads: Ref<Record<string, DropZoneUpload>>
  refIsUploading: ComputedRef<boolean>
  refCanSend: ComputedRef<boolean>
  setCategory: (option: NativeSelectOption) => void
  setTask: (option: NativeSelectOption) => void
  setComment: (event: AppTextareaEvent) => void
  uploadFiles: (files: DropZoneAcceptedFile[]) => void
  retryFile: (payload: DropZoneItemPayload) => void
  removeFile: (payload: DropZoneItemPayload) => void
  closeModal: () => void
  sendFiles: () => Promise<void>
}

export const useModalDisciplineFilesSend = (): ModalDisciplineFilesSendReturn => {
  const route = useRoute()
  const disciplineStore = useDisciplineStore()
  const store = useDisciplineFilesStore()
  const listsStore = useListsStore()

  const refIsLoading = ref(false)
  const refPackId = ref<number | null>(null)
  const refCategory = ref('')
  const refTutorId = ref<number | null>(null)
  const refTaskId = ref<number | null>(null)
  const refComment = ref('')
  const refUploads = ref<Record<string, DropZoneUpload>>({})
  // DropZone ids → uploaded file ids, and the ids removed while their upload was still running.
  const uploadedFileIds = new Map<string, number>()
  const removedWhileUploading = new Set<string>()

  const disciplineId = (): string => route.params.id as string

  const refIsOpen = computed<boolean>(() => store.getActiveModal === DisciplineFilesModal.Send)

  const refTitle = computed<string>(() => {
    const kid = disciplineStore.getDiscipline?.kid
    return kid?.name ? `Files to: ${kid.name} (${kid.grade ?? ''})` : 'Send files'
  })

  const refCategoryOptions = computed<NativeSelectOption[]>(() =>
    Object.entries(listsStore.getLists?.file.category ?? {}).map(([value, text]) => ({
      id: value,
      value,
      text,
      selected: value === refCategory.value,
    })))

  const refTaskOptions = computed<NativeSelectOption[]>(() =>
    store.getTasks.map(task => ({
      id: task.id,
      value: task.id,
      text: getTaskOptionText(task),
      selected: task.id === refTaskId.value,
    })))

  const refIsUploading = computed<boolean>(() =>
    Object.values(refUploads.value).some(upload => upload.status === DROP_ZONE_STATUS.Uploading))

  // The server sends an empty draft too; the legacy form required at least one uploaded file.
  const refCanSend = computed<boolean>(() =>
    !!refPackId.value && !refIsUploading.value
    && Object.values(refUploads.value).some(upload => upload.status === DROP_ZONE_STATUS.Success))

  const setUpload = (id: string, upload: DropZoneUpload): void => {
    refUploads.value = { ...refUploads.value, [id]: upload }
  }

  const dropUpload = (id: string): void => {
    refUploads.value = Object.fromEntries(Object.entries(refUploads.value).filter(([key]) => key !== id))
  }

  const deleteUploadedFile = async (id: string): Promise<void> => {
    const fileId = uploadedFileIds.get(id)
    if (!fileId) return

    uploadedFileIds.delete(id)
    await useDisciplineFileDeleteApi({ disciplineId: disciplineId(), fileId })
  }

  const uploadFile = async ({ id, file }: DropZoneAcceptedFile): Promise<void> => {
    const packId = refPackId.value
    if (!packId) return

    setUpload(id, { status: DROP_ZONE_STATUS.Uploading, progress: 0 })

    const uploaded = await useDisciplineFileUploadApi({
      disciplineId: disciplineId(),
      packId,
      file,
      onProgress: progress => setUpload(id, { status: DROP_ZONE_STATUS.Uploading, progress }),
      onError: error => setUpload(id, { status: DROP_ZONE_STATUS.Error, error }),
    })

    if (!uploaded) return

    uploadedFileIds.set(id, uploaded.id)

    if (removedWhileUploading.delete(id)) {
      await deleteUploadedFile(id)
      return
    }

    setUpload(id, { status: DROP_ZONE_STATUS.Success, progress: 100, viewLink: uploaded.view_link })
  }

  const uploadFiles = (files: DropZoneAcceptedFile[]): void => {
    files.forEach(file => void uploadFile(file))
  }

  const retryFile = (payload: DropZoneItemPayload): void => {
    void uploadFile(payload)
  }

  const removeFile = ({ id }: DropZoneItemPayload): void => {
    if (refUploads.value[id]?.status === DROP_ZONE_STATUS.Uploading) removedWhileUploading.add(id)
    dropUpload(id)
    void deleteUploadedFile(id)
  }

  const setCategory = (option: NativeSelectOption): void => {
    refCategory.value = String(option.value)
  }

  const setTask = (option: NativeSelectOption): void => {
    refTaskId.value = option.value === null ? null : Number(option.value)
  }

  const setComment = (event: AppTextareaEvent): void => {
    refComment.value = event.value
  }

  const resetState = (): void => {
    refPackId.value = null
    refCategory.value = Object.keys(listsStore.getLists?.file.category ?? {})[0] ?? ''
    refTutorId.value = null
    refTaskId.value = null
    refComment.value = ''
    refUploads.value = {}
    uploadedFileIds.clear()
    removedWhileUploading.clear()
  }

  /** A cancelled draft that already holds files is never reused by the API, so it is discarded. */
  const closeModal = (): void => {
    if (refIsLoading.value) return

    const packId = refPackId.value
    if (packId && uploadedFileIds.size > 0) {
      void useDisciplineFilePackDeleteApi({ disciplineId: disciplineId(), packId, silent: true })
    }

    store.closeModal()
  }

  const openDraft = async (): Promise<void> => {
    const draft = await useDisciplineFilePackStoreApi({
      disciplineId: disciplineId(),
      onStart: () => { refIsLoading.value = true },
      onFinish: () => { refIsLoading.value = false },
    })

    if (!draft) {
      store.closeModal()
      return
    }

    refPackId.value = draft.id
  }

  const sendFiles = async (): Promise<void> => {
    const packId = refPackId.value
    if (!packId || !refCanSend.value || refIsLoading.value) return

    const sent = await useDisciplineFilePackSendApi({
      disciplineId: disciplineId(),
      packId,
      payload: {
        category: refCategory.value,
        comment: refComment.value.trim() || null,
        tutor_id: refTutorId.value,
        ...(refTaskId.value && { task_id: refTaskId.value }),
      },
      onStart: () => { refIsLoading.value = true },
      onFinish: () => { refIsLoading.value = false },
    })

    if (!sent) return

    uploadedFileIds.clear()
    store.closeModal()
    await useDisciplineFilesApi({ disciplineId: disciplineId() })
  }

  watch(refIsOpen, (isOpen) => {
    if (!isOpen) return

    resetState()
    void openDraft()
  })

  return {
    refIsOpen,
    refIsLoading,
    refTitle,
    refCategoryOptions,
    refTaskOptions,
    refCategory,
    refTutorId,
    refTaskId,
    refComment,
    refUploads,
    refIsUploading,
    refCanSend,
    setCategory,
    setTask,
    setComment,
    uploadFiles,
    retryFile,
    removeFile,
    closeModal,
    sendFiles,
  }
}
